import { useForm } from 'react-hook-form'
import BasicSlide from './BasicSlide'
import countries from 'countries-list'
import { useUser } from '../contexts/userContext'
import { Button, Form, FormControl, InputGroup } from 'react-bootstrap'
import AccordianSelect from './AccordianSelect'
import { useEffect } from 'react'
import { track } from '../services/analytics'

const errorMessage = (errors, input) => (
  <Form.Control.Feedback type='invalid'>
    <small>{errors[input.label]?.message}</small>
  </Form.Control.Feedback>
)

const errorMessageRequired = 'This field is required'

const TextBoxGroup = ({ input, register, errors, ...props }) => {
  return (
    <Form.Group className='pb-3' key={input.label}>
      {input?.text && <Form.Label>{input.text}</Form.Label>}
      <Form.Control
        {...register(input.label, input.errorHandling)}
        name={input.label}
        placeholder={input.placeholder}
        isInvalid={!!errors[input.label]}
        {...props}
      />
      {errorMessage(errors, input)}
    </Form.Group>
  )
}
const TextButtonGroup = ({
  input,
  register,
  errors,
  buttonText,
  buttonOnClick,
  ...props
}) => {
  return (
    <InputGroup key={input.label} size={input?.size || 'md'}>
      <FormControl
        {...register(input.label, input.errorHandling)}
        name={input.label}
        placeholder={input.placeholder}
        isInvalid={!!errors[input.label]}
        {...props}
      />
      <Button variant='primary' onClick={buttonOnClick}>
        {buttonText}
      </Button>
      {errorMessage(errors, input)}
    </InputGroup>
  )
}

const Select = ({ input, register, errors }) => {
  const { options } = input
  return (
    <Form.Group className='pb-3' key={input.label}>
      <Form.Label>{input.text}</Form.Label>
      <Form.Select
        aria-label={input.label}
        {...register(input.label, input.errorHandling)}
      >
        <option value=''>{input.placeholder}</option>
        {options.map((option, idx) => (
          <option value={option.value} key={idx}>
            {option.text}
          </option>
        ))}
      </Form.Select>
      {errorMessage(errors, input)}
      {/* <Form.Control.Feedback type='invalid'>
        {errors[input.label]?.message}
      </Form.Control.Feedback> */}
    </Form.Group>
  )
}

const CheckGroup = ({ type, input, register, ...props }) => {
  const { label, text, errorHandling, ...inputProps } = input
  return (
    <Form.Check
      {...inputProps}
      {...register(label, errorHandling)}
      {...props}
      type={type}
      label={text}
    />
  )
}

const DiscordGroup = ({ input, register, errors }) => {
  input.errorHandling = {
    required: errorMessageRequired,
    pattern: {
      value: /^.{3,32}#[0-9]{4}$/,
      message: 'Entered value does not match discord username format'
    }
  }
  return <TextBoxGroup input={input} register={register} errors={errors} />
}

const EmailGroup = ({
  input,
  TextInput = TextBoxGroup,
  register,
  errors,
  ...props
}) => {
  input.errorHandling = {
    required: errorMessageRequired,
    pattern: {
      value: /\S+@\S+\.\S+/,
      message: 'Please enter a valid email'
    }
  }
  return (
    <TextInput input={input} register={register} errors={errors} {...props} />
  )
}

const SelectRequired = ({ inputs: input, register, errors }) => {
  input.errorHandling = {
    required: errorMessageRequired,
    pattern: {
      value: input.placeholder,
      message: 'Please select a value'
    }
  }
  return <Select input={input} register={register} errors={errors} />
}

const SelectLocation = ({ input, register, errors }) => {
  const countryCodes = Object.keys(countries.countries)
  input.options = countryCodes
    .map(code => ({
      value: countries.countries[code].name,
      text: countries.countries[code].name
    }))
    .sort((a, b) => a.text.localeCompare(b.text))

  return <Select input={input} register={register} errors={errors} />
}

const FormElementMapper = (
  input,
  idx,
  register,
  errors,
  watch,
  setValue,
  submitFormData
) => {
  const { type, ...props } = input

  switch (input.type) {
    case 'parent':
      const { Parent, children, ...parentProps } = props
      return (
        <Parent key={idx} {...parentProps}>
          {children.map((cInput, cIdx) =>
            FormElementMapper(cInput, cIdx, register, errors)
          )}
        </Parent>
      )

    case 'html':
      const { html, ...elementProps } = props
      return (
        <div
          key={idx}
          dangerouslySetInnerHTML={{ __html: html }}
          {...elementProps}
        />
      )

    case 'text-box':
      return (
        <TextBoxGroup
          input={input}
          register={register}
          errors={errors}
          key={idx}
        />
      )
    case 'big-text-box':
      return (
        <TextBoxGroup
          input={input}
          register={register}
          errors={errors}
          key={idx}
          rows={4}
          as='textarea'
        />
      )
    case 'email-text-box':
      return (
        <EmailGroup
          input={input}
          register={register}
          errors={errors}
          key={idx}
        />
      )
    case 'email-input-submit-button':
      return (
        <EmailGroup
          TextInput={TextButtonGroup}
          input={input}
          register={register}
          errors={errors}
          key={idx}
          buttonText={input.buttonText}
          buttonOnClick={submitFormData}
        />
      )
    case 'discord-text-box':
      return (
        <DiscordGroup
          input={input}
          register={register}
          errors={errors}
          key={idx}
        />
      )
    case 'text':
      return (
        <Form.Label key={idx} {...input}>
          {input.text}
        </Form.Label>
      )
    case 'switch':
      return (
        <CheckGroup type='switch' input={input} register={register} key={idx} />
      )
    case 'radio-inline':
      return (
        <CheckGroup
          type='radio'
          className='col text-center'
          input={input}
          register={register}
          key={idx}
        />
      )
    case 'select':
      return (
        <Select input={input} register={register} errors={errors} key={idx} />
      )
    case 'select-location':
      return (
        <SelectLocation
          input={input}
          register={register}
          errors={errors}
          key={idx}
        />
      )
    case 'select-required':
      return (
        <SelectRequired
          input={input}
          register={register}
          errors={errors}
          key={idx}
        />
      )
    case 'accordian':
      return (
        <AccordianSelect
          input={input}
          register={register}
          errors={errors}
          setValue={setValue}
          watch={watch}
          key={idx}
        />
      )
    case 'image':
      return (
        <img
          className='form-image'
          alt='nonsense'
          src={input.src}
          key={idx}
          {...props}
        />
      )
    default:
      return input.element
  }
}

export default function FormSlide ({
  SlideLayout = BasicSlide,
  rightContentHeader,
  formData,
  defaultValData,
  onFormSubmit,
  nextTo,
  saveUser = true,
  ...props
}) {
  const { user, setUser } = useUser()

  const {
    register,
    handleSubmit,
    formState,
    formState: { errors },
    getValues,
    setValue,
    reset,
    watch
  } = useForm({ mode: 'onChange', defaultValues: {} })
  const onSubmit = data => console.log('submitted:', data)

  useEffect(() => {
    const defaultValues = {}
    const defValData = defaultValData || formData
    defValData.forEach(({ label }) => {
      if (user.hasOwnProperty(label)) {
        defaultValues[label] = user[label]
      }
    })

    // console.log('defaultValues', defaultValues)
    reset(defaultValues)
  }, [user])

  // console.log('isDirty', formState.isDirty)
  // console.log('isValid', formState.isValid)
  // console.log('errors', errors)
  // console.log('getValues', getValues())

  const submitFormData = async () => {
    const values = getValues()
    console.log('Saving getValues', values)
    setUser(currUser => ({ ...currUser, ...values }))

    track('submitted form', values)

    if (onFormSubmit) {
      await onFormSubmit(values)
    }
  }

  return (
    <SlideLayout
      onNextClick={submitFormData}
      nextTo={formState.isValid ? nextTo : null}
      rightContent={
        <>
          {rightContentHeader}
          <Form
            className='left-form'
            noValidate
            onSubmit={handleSubmit(onSubmit)}
          >
            {formData.map((input, idx) =>
              FormElementMapper(
                input,
                idx,
                register,
                errors,
                watch,
                setValue,
                formState.isValid ? submitFormData : () => {}
              )
            )}
          </Form>
        </>
      }
      {...props}
    />
  )
}
